import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../FadeIn';

const categories = [
  {
    label: "Positioning & Strategy",
    title: "Know exactly who you are.",
    desc: "The strategic foundation — your brand thesis, competitive position, and the rationale behind every decision.",
    deliverables: [
      "Brand thesis document (one-sentence positioning + full rationale)",
      "Competitive positioning audit & gap analysis",
      "Stakeholder-ready positioning deck"
    ],
    usage: "Becomes the single source of truth for every decision your organization makes — from hiring to product to fundraising.",
    impact: "One client's positioning document replaced 14 separate strategy decks and became the most-referenced artifact in their company.",
    partner: null
  },
  {
    label: "Messaging & Narrative",
    title: "Say it so it sticks.",
    desc: "The words that carry your positioning into the world — from landing pages to sales calls to investor rooms.",
    deliverables: [
      "Messaging framework & voice architecture",
      "Sales narrative with objection handling",
      "Pitch-ready strategic materials & proposal frameworks"
    ],
    usage: "Deployed across every touchpoint — outbound, inbound, pitch meetings, job postings. Ensures the brand sounds like one voice everywhere.",
    impact: "A Series A SaaS rewrote their outbound using this framework. Discovery call advancement went from 22% to 67%.",
    partner: null
  },
  {
    label: "Go-to-Market",
    title: "Get it into the market.",
    desc: "The operational blueprint that takes your positioning from document to revenue — targeting, sequencing, and measurement.",
    deliverables: [
      "ICP research & targeting documentation",
      "Launch sequencing with stage gates & KPIs",
      "Pipeline architecture (end-to-end flow with tooling)"
    ],
    usage: "The execution layer. Tells your team exactly who to target, what to say, in what order, measured by what. Not theory — operations.",
    impact: "A pre-launch product went from planning paralysis to first paying customer in 9 weeks. Roadmap cut from 40 features to 8.",
    partner: {
      text: "For embedded GTM execution, we partner with",
      name: "FirstShip Partners",
      desc: "fractional product leadership from roadmap to market.",
      url: "https://firstshippartners.com"
    }
  },
  {
    label: "Execution & Systems",
    title: "Build it to last.",
    desc: "The operational layer that turns strategy into daily reality — visual systems, brand governance, and team alignment.",
    deliverables: [
      "Visual identity & brand system (palette, typography, creative brief)",
      "Internal alignment playbook (SOPs, cadence, handoff docs)",
      "Deployment documentation built for scale"
    ],
    usage: "Handed to your team, your designers, your sales org. Built for deployment and handoff — not dependency on us.",
    impact: "A brand system built on these principles was deployed across 4 product lines, maintaining coherence without a dedicated design team.",
    partner: {
      text: "For ongoing operational support, our ecosystem includes",
      name: "WeekOne Systems",
      desc: "AI-powered executive operations.",
      url: "https://wk1.ai"
    }
  }
];

const panelTransition = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }
};

const DetailContent = ({ cat }) => (
  <div>
    <p className="text-sm text-c-dark/50 font-light leading-relaxed mb-5">
      {cat.desc}
    </p>

    <div className="mb-5">
      <p className="text-[10px] font-medium tracking-widest uppercase text-c-mauve/70 mb-3">You Receive</p>
      <ul className="space-y-2">
        {cat.deliverables.map((line, i) => (
          <li key={i} className="text-sm text-c-dark/60 font-light flex items-start gap-2.5">
            <span className="text-c-mauve/50 mt-0.5 flex-shrink-0">—</span>
            {line}
          </li>
        ))}
      </ul>
    </div>

    <div className="mb-5">
      <p className="text-[10px] font-medium tracking-widest uppercase text-c-mauve/70 mb-2">How It's Deployed</p>
      <p className="text-sm text-c-dark/50 font-light leading-relaxed">{cat.usage}</p>
    </div>

    <div className="pl-4 border-l-2 border-c-mauve/25 mb-5">
      <p className="text-sm text-c-dark/60 font-light leading-relaxed italic">
        "{cat.impact}"
      </p>
    </div>

    {cat.partner && (
      <div className="pt-4 border-t border-c-dark/6">
        <p className="text-xs text-c-dark/40 font-light">
          {cat.partner.text}{' '}
          <a
            href={cat.partner.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-c-mauve/70 hover:text-c-mauve transition-colors font-medium"
          >
            {cat.partner.name}
          </a>
          {' — '}{cat.partner.desc}
        </p>
      </div>
    )}
  </div>
);

const DeliverablesAndSignal = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const active = activeIndex !== null ? categories[activeIndex] : null;

  const handleSelect = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="relative py-32 md:py-48 bg-brand-light text-c-dark">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-20 md:mb-28 max-w-2xl">
          <FadeIn>
            <h2 className="text-xs font-medium tracking-super-wide uppercase text-c-dark/40 mb-6">What You Walk Away With</h2>
            <h3 className="text-3xl md:text-5xl font-light text-c-dark leading-[1.2]">
              Strategy you can <span className="font-serif-italic text-c-mauve">deploy.</span><br />
              Not file away.
            </h3>
          </FadeIn>
        </div>

        {/* Desktop: side-by-side */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-24">
          <div>
            {categories.map((cat, index) => (
              <FadeIn key={index} delay={index * 0.08}>
                <button
                  onClick={() => handleSelect(index)}
                  className={`w-full text-left py-8 transition-all duration-400 rounded-xl px-6 -mx-6 ${
                    activeIndex === index ? 'bg-c-dark/[0.04]' : 'hover:bg-c-dark/[0.02]'
                  }`}
                >
                  <div className="flex items-center gap-5">
                    <span
                      className="block w-3 h-3 rounded-full flex-shrink-0 transition-all duration-300"
                      style={{
                        background: activeIndex === index
                          ? 'linear-gradient(135deg, var(--c-mauve), var(--c-coral))'
                          : 'rgba(0,0,0,0.08)',
                        transform: activeIndex === index ? 'scale(1.15)' : 'scale(1)'
                      }}
                    />
                    <div className="flex-1">
                      <p className={`text-[10px] font-medium tracking-widest uppercase mb-1.5 transition-colors duration-300 ${
                        activeIndex === index ? 'text-c-mauve/70' : 'text-c-dark/25'
                      }`}>
                        {cat.label}
                      </p>
                      <h4 className={`text-xl md:text-2xl font-light transition-colors duration-300 ${
                        activeIndex === index ? 'text-c-dark' : 'text-c-dark/50'
                      }`}>
                        {cat.title}
                      </h4>
                    </div>
                  </div>
                </button>
              </FadeIn>
            ))}
          </div>

          <div>
            <div className="lg:sticky lg:top-32">
              <AnimatePresence mode="wait">
                {active ? (
                  <motion.div key={activeIndex} {...panelTransition}>
                    <DetailContent cat={active} />
                  </motion.div>
                ) : (
                  <motion.div key="default" {...panelTransition}>
                    <div className="pl-6 border-l-2 border-c-mauve/25">
                      <blockquote className="text-xl md:text-2xl text-c-dark/50 font-light leading-relaxed italic mb-8">
                        "Lee didn't just rebrand us — he rewired how we think about our market. The positioning document became the single most referenced artifact in our company."
                      </blockquote>
                      <div className="mb-16">
                        <p className="text-sm font-medium text-c-dark/60">Founder</p>
                        <p className="text-xs text-c-dark/40 tracking-wide">Series A SaaS</p>
                      </div>
                    </div>
                    <p className="text-sm text-c-dark/30 font-light leading-relaxed">
                      Every engagement is backed by the <span className="font-wordmark text-c-dark/40">chRsMa</span> ecosystem — a portfolio of operator-led companies that extend strategy into execution.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile: accordion with vertical connector */}
        <div className="lg:hidden">
          {categories.map((cat, index) => (
            <FadeIn key={index} delay={index * 0.08}>
              <div className="relative flex">
                {/* Vertical line + dot column */}
                <div className="flex flex-col items-center mr-4 flex-shrink-0">
                  <span
                    className="block w-2.5 h-2.5 rounded-full mt-5 transition-all duration-300 flex-shrink-0 z-10"
                    style={{
                      background: activeIndex === index
                        ? 'linear-gradient(135deg, var(--c-mauve), var(--c-coral))'
                        : 'rgba(0,0,0,0.1)',
                    }}
                  />
                  {/* Connector line */}
                  <div className={`w-[1px] flex-1 transition-colors duration-300 ${
                    activeIndex === index ? 'bg-c-mauve/30' : 'bg-c-dark/8'
                  }`} />
                </div>

                {/* Content column */}
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => handleSelect(index)}
                    className="w-full text-left py-3"
                  >
                    <p className={`text-[10px] font-medium tracking-widest uppercase mb-1 transition-colors duration-300 ${
                      activeIndex === index ? 'text-c-mauve/70' : 'text-c-dark/25'
                    }`}>
                      {cat.label}
                    </p>
                    <h4 className={`font-light transition-all duration-300 ${
                      activeIndex === index ? 'text-c-dark text-xl' : 'text-c-dark/50 text-base'
                    }`}>
                      {cat.title}
                    </h4>
                  </button>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-4 pt-1">
                          <DetailContent cat={cat} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DeliverablesAndSignal;
